import { useState, useEffect } from 'react';
import logger from 'services/logger';

export const useLocalStorage = <T>(key: string, initialValue?: T) => {
  const getStoredValue = (): T | null => {
    if (typeof window === 'undefined') return initialValue || null;

    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue || null;
    } catch (error) {
      logger.warn('Error reading from localStorage:', error);
      return initialValue || null;
    }
  };

  const [value, setValue] = useState<T | null>(getStoredValue);

  const hasKey = (): boolean => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(key) !== null;
  };

  const setStoredValue = (newValue: T | null): void => {
    try {
      if (newValue === null || newValue === undefined) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, JSON.stringify(newValue));
      }
      setValue(newValue);
    } catch (error) {
      logger.warn('Error writing to localStorage:', error);
    }
  };

  const removeValue = (): void => {
    setStoredValue(null);
  };

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key) {
        setValue(getStoredValue());
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [key]);

  return {
    value,
    hasKey,
    setValue: setStoredValue,
    removeValue,
  };
};
